import React from 'react';
import { ArrowRight, Target, Calculator, TrendingUp, Shield, Bot, Sparkles } from 'lucide-react';

interface NosSimulateursProps {
  onNavigateBack: () => void;
  onStartSimulateurStrategies: () => void;
  onStartSimulator: () => void;
  onStartGuidedInvestment: () => void;
  onStartSimulateurInvestissement: () => void;
}

const NosSimulateurs: React.FC<NosSimulateursProps> = ({ 
  onNavigateBack, 
  onStartSimulateurStrategies, 
  onStartSimulator,
  onStartGuidedInvestment,
  onStartSimulateurInvestissement
}) => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header avec retour */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={onNavigateBack}
              className="flex items-center space-x-2 text-gray-600 hover:text-teal-600 transition-colors"
            >
              <span>←</span>
              <span>Retour à l'accueil</span>
            </button>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-r from-teal-500 to-blue-500 rounded-lg flex items-center justify-center">
                <Calculator className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-xl font-bold text-gray-900">
                Nos Simulateurs
              </h1>
            </div>
            <div className="w-32"></div> {/* Spacer pour centrer le titre */}
          </div>
        </div>
      </header>

      {/* Section Découvrez les 3 Stratégies Gagnantes */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-red-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Contenu */}
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="inline-flex items-center bg-gradient-to-r from-orange-100 to-red-100 text-orange-700 px-4 py-2 rounded-full text-sm font-semibold">
                  <Target className="w-4 h-4 mr-2" />
                  <span>3 stratégies optimisées</span>
                </div>
                
                <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                  Découvrez les{' '}
                  <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                    3 Stratégies
                  </span>{' '}
                  Gagnantes
                </h2>
                
                <p className="text-xl text-gray-600 leading-relaxed">
                  Chaque profil d'investisseur mérite une approche sur mesure. Découvrez quelle stratégie 
                  correspond le mieux à vos objectifs et votre appétence au risque.
                </p>
              </div>

              {/* Profils stratégiques */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-green-50 rounded-xl border border-green-100">
                  <div className="w-10 h-10 rounded-lg overflow-hidden shadow-lg mx-auto mb-2">
                    <img 
                      src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop&crop=center"
                      alt="Stratégie Protecteur - Sécurisée"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="font-semibold text-gray-900 text-sm">Protecteur</h3>
                  <p className="text-xs text-green-600 font-medium">3.5%</p>
                </div>
                
                <div className="text-center p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <div className="w-10 h-10 rounded-lg overflow-hidden shadow-lg mx-auto mb-2">
                    <img 
                      src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&h=300&fit=crop&crop=center"
                      alt="Stratégie Équilibrée - Optimisée"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="font-semibold text-gray-900 text-sm">Équilibré</h3>
                  <p className="text-xs text-blue-600 font-medium">5.5%</p>
                </div>
                
                <div className="text-center p-4 bg-orange-50 rounded-xl border border-orange-100">
                  <div className="w-10 h-10 rounded-lg overflow-hidden shadow-lg mx-auto mb-2">
                    <img 
                      src="https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400&h=300&fit=crop&crop=center"
                      alt="Stratégie Dynamique - Performante"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="font-semibold text-gray-900 text-sm">Dynamique</h3>
                  <p className="text-xs text-orange-600 font-medium">8.5%</p>
                </div>
              </div>

              <div className="pt-4">
                <button
                  onClick={onStartSimulateurStrategies}
                  className="group bg-gradient-to-r from-orange-600 to-red-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-orange-700 hover:to-red-700 transition-all duration-300 flex items-center space-x-3 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  <Target className="w-6 h-6" />
                  <span>Choisir ma stratégie</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>

            {/* Courbe moderne interactive des stratégies */}
            <div className="relative bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 rounded-2xl p-8 overflow-hidden">
              {/* Background pattern moderne */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M0 0h80v80H0z'/%3E%3Cpath d='M20 20h40v40H20z' fill-opacity='0.03'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                }}></div>
              </div>
              
              {/* Titre de la courbe */}
              <div className="text-center mb-6">
                <h3 className="text-white text-xl font-bold mb-2">Performance par Profil de Risque</h3>
                <p className="text-white/60 text-sm">Évolution du rendement selon votre stratégie</p>
              </div>
              
              {/* SVG Courbe interactive */}
              <div className="relative h-64 mb-6">
                <svg className="w-full h-full" viewBox="0 0 400 200" fill="none">
                  {/* Grille de fond */}
                  <defs>
                    <pattern id="modernGrid" width="40" height="40" patternUnits="userSpaceOnUse">
                      <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
                    </pattern>
                    <linearGradient id="curveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" style={{stopColor: '#10b981', stopOpacity: 0.8}} />
                      <stop offset="50%" style={{stopColor: '#3b82f6', stopOpacity: 0.8}} />
                      <stop offset="100%" style={{stopColor: '#f59e0b', stopOpacity: 0.8}} />
                    </linearGradient>
                    <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" style={{stopColor: 'url(#curveGradient)', stopOpacity: 0.3}} />
                      <stop offset="100%" style={{stopColor: 'url(#curveGradient)', stopOpacity: 0}} />
                    </linearGradient>
                  </defs>
                  
                  {/* Grille */}
                  <rect width="400" height="200" fill="url(#modernGrid)" />
                  
                  {/* Axes */}
                  <line x1="40" y1="160" x2="360" y2="160" stroke="rgba(255,255,255,0.2)" strokeWidth="2"/>
                  <line x1="40" y1="40" x2="40" y2="160" stroke="rgba(255,255,255,0.2)" strokeWidth="2"/>
                  
                  {/* Labels des axes */}
                  <text x="50" y="175" fill="rgba(255,255,255,0.6)" fontSize="12" fontFamily="sans-serif">Protecteur</text>
                  <text x="180" y="175" fill="rgba(255,255,255,0.6)" fontSize="12" fontFamily="sans-serif">Équilibré</text>
                  <text x="300" y="175" fill="rgba(255,255,255,0.6)" fontSize="12" fontFamily="sans-serif">Dynamique</text>
                  
                  <text x="15" y="165" fill="rgba(255,255,255,0.6)" fontSize="10" fontFamily="sans-serif">3%</text>
                  <text x="15" y="120" fill="rgba(255,255,255,0.6)" fontSize="10" fontFamily="sans-serif">5%</text>
                  <text x="15" y="75" fill="rgba(255,255,255,0.6)" fontSize="10" fontFamily="sans-serif">7%</text>
                  <text x="15" y="50" fill="rgba(255,255,255,0.6)" fontSize="10" fontFamily="sans-serif">8%</text>
                  
                  {/* Zone sous la courbe */}
                  <path
                    d="M 80 140 Q 200 100 340 60 L 340 160 L 80 160 Z"
                    fill="url(#areaGradient)"
                  />
                  
                  {/* Courbe principale */}
                  <path
                    d="M 80 140 Q 200 100 340 60"
                    stroke="url(#curveGradient)"
                    strokeWidth="4"
                    fill="none"
                    strokeLinecap="round"
                  >
                    <animate attributeName="stroke-dasharray" values="0 1000;1000 0" dur="3s" fill="freeze"/>
                  </path>
                  
                  {/* Points interactifs */}
                  {/* Protecteur */}
                  <g className="group cursor-pointer">
                    <circle cx="80" cy="140" r="8" fill="#10b981" stroke="white" strokeWidth="2" opacity="0.9">
                      <animate attributeName="r" values="6;10;6" dur="2s" repeatCount="indefinite"/>
                    </circle>
                    <circle cx="80" cy="140" r="15" fill="rgba(16, 185, 129, 0.2)" opacity="0">
                      <animate attributeName="opacity" values="0;0.6;0" dur="2s" repeatCount="indefinite"/>
                      <animate attributeName="r" values="15;25;15" dur="2s" repeatCount="indefinite"/>
                    </circle>
                  </g>
                  
                  {/* Équilibré */}
                  <g className="group cursor-pointer">
                    <circle cx="200" cy="100" r="8" fill="#3b82f6" stroke="white" strokeWidth="2" opacity="0.9">
                      <animate attributeName="r" values="6;10;6" dur="2s" repeatCount="indefinite" begin="0.5s"/>
                    </circle>
                    <circle cx="200" cy="100" r="15" fill="rgba(59, 130, 246, 0.2)" opacity="0">
                      <animate attributeName="opacity" values="0;0.6;0" dur="2s" repeatCount="indefinite" begin="0.5s"/>
                      <animate attributeName="r" values="15;25;15" dur="2s" repeatCount="indefinite" begin="0.5s"/>
                    </circle>
                  </g>
                  
                  {/* Dynamique */}
                  <g className="group cursor-pointer">
                    <circle cx="340" cy="60" r="8" fill="#f59e0b" stroke="white" strokeWidth="2" opacity="0.9">
                      <animate attributeName="r" values="6;10;6" dur="2s" repeatCount="indefinite" begin="1s"/>
                    </circle>
                    <circle cx="340" cy="60" r="15" fill="rgba(245, 158, 11, 0.2)" opacity="0">
                      <animate attributeName="opacity" values="0;0.6;0" dur="2s" repeatCount="indefinite" begin="1s"/>
                      <animate attributeName="r" values="15;25;15" dur="2s" repeatCount="indefinite" begin="1s"/>
                    </circle>
                  </g>
                </svg>
                
                {/* Tooltips flottants */}
                <div className="absolute top-8 left-16 bg-black/80 backdrop-blur-sm rounded-lg p-3 border border-green-400/30 transform -translate-y-2">
                  <div className="text-green-400 text-sm font-semibold">Protecteur</div>
                  <div className="text-white text-lg font-bold">3.5%</div>
                  <div className="text-white/60 text-xs">Risque faible</div>
                </div>
                
                <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-black/80 backdrop-blur-sm rounded-lg p-3 border border-blue-400/30">
                  <div className="text-blue-400 text-sm font-semibold">Équilibré</div>
                  <div className="text-white text-lg font-bold">5.5%</div>
                  <div className="text-white/60 text-xs">Risque modéré</div>
                </div>
                
                <div className="absolute top-2 right-8 bg-black/80 backdrop-blur-sm rounded-lg p-3 border border-orange-400/30">
                  <div className="text-orange-400 text-sm font-semibold">Dynamique</div>
                  <div className="text-white text-lg font-bold">8.5%</div>
                  <div className="text-white/60 text-xs">Risque élevé</div>
                </div>
              </div>
              
              {/* Statistiques modernes */}
              <div className="grid grid-cols-3 gap-4 mt-6">
                <div className="text-center p-4 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-all duration-300">
                  <div className="text-green-400 text-2xl font-bold">3.5%</div>
                  <div className="text-white text-sm">Sécurisé</div>
                  <div className="text-white/50 text-xs">Faible volatilité</div>
                </div>
                
                <div className="text-center p-4 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-all duration-300">
                  <div className="text-blue-400 text-2xl font-bold">5.5%</div>
                  <div className="text-white text-sm">Optimisé</div>
                  <div className="text-white/50 text-xs">Équilibre parfait</div>
                </div>
                
                <div className="text-center p-4 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-all duration-300">
                  <div className="text-orange-400 text-2xl font-bold">8.5%</div>
                  <div className="text-white text-sm">Ambitieux</div>
                  <div className="text-white/50 text-xs">Haute performance</div>
                </div>
              </div>
              
              {/* Particules flottantes décoratives */}
              <div className="absolute top-10 left-10 w-1 h-1 bg-white/40 rounded-full animate-ping" style={{animationDelay: '1s'}}></div>
              <div className="absolute bottom-10 right-20 w-1.5 h-1.5 bg-purple-300/50 rounded-full animate-pulse" style={{animationDelay: '2s'}}></div>
              <div className="absolute top-20 right-10 w-1 h-1 bg-blue-300/60 rounded-full animate-bounce" style={{animationDelay: '3s'}}></div>
            </div>
          </div>
        </div>
      </section>

      {/* Mon Bilan PDF Section */}
      <section id="bilan-pdf" className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            
            {/* Content Side */}
            <div className="space-y-8">
              <div className="space-y-6">
                <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-teal-100 to-blue-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Analyse complète et personnalisée</span>
                </div>
                
                <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                  Obtenez votre{' '}
                  <span className="bg-gradient-to-r from-teal-500 to-blue-500 bg-clip-text text-transparent">
                    Bilan Patrimonial
                  </span>{' '}
                  complet
                </h2>
                
                <p className="text-xl text-gray-600 leading-relaxed">
                  Recevez un rapport PDF détaillé avec vos recommandations d'investissement 
                  personnalisées, vos projections de rendement et votre stratégie patrimoniale optimisée.
                </p>
              </div>

              {/* Features List */}
              <div className="space-y-4">
                {[
                  {
                    icon: (
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    ),
                    title: "Analyse de votre profil investisseur",
                    description: "Évaluation complète de votre situation financière et de vos objectifs"
                  },
                  {
                    icon: (
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                    ),
                    title: "Projections de performance",
                    description: "Simulations détaillées sur 5, 10 et 15 ans avec différents scénarios"
                  },
                  {
                    icon: (
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                      </svg>
                    ),
                    title: "Allocation d'actifs optimisée",
                    description: "Répartition personnalisée entre immobilier, finance éthique et forestier"
                  },
                  {
                    icon: (
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    ),
                    title: "Recommandations certifiées",
                    description: "Conseils validés par nos experts financiers et conformes AMF"
                  }
                ].map((feature, index) => (
                  <div key={index} className="flex items-start space-x-4 p-4 rounded-xl bg-white/50 hover:bg-white/80 transition-all duration-300">
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-teal-500 to-blue-500 rounded-lg flex items-center justify-center text-white">
                      {feature.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">{feature.title}</h3>
                      <p className="text-gray-600 text-sm">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <div className="pt-4">
                <button
                  onClick={onStartSimulator}
                  className="group bg-gradient-to-r from-teal-500 to-blue-500 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:from-teal-600 hover:to-blue-600 transition-all duration-300 flex items-center space-x-3 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Générer mon bilan PDF gratuit</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>

            {/* Visual Side - PDF Preview */}
            <div className="relative">
              <div className="relative max-w-md mx-auto">
                {/* Background Glow */}
                <div className="absolute inset-0 bg-gradient-to-r from-teal-200 to-blue-200 rounded-2xl blur-xl opacity-30 animate-pulse"></div>
                
                {/* PDF Preview */}
                <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
                  {/* PDF Header */}
                  <div className="bg-gradient-to-r from-teal-500 to-blue-500 px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                          <Calculator className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h3 className="text-white font-semibold">SOeasi</h3>
                          <p className="text-white/80 text-xs">Bilan Patrimonial</p>
                        </div>
                      </div>
                      <div className="text-white/80 text-xs">
                        PDF
                      </div>
                    </div>
                  </div>
                  
                  {/* PDF Content Preview */}
                  <div className="p-6 space-y-4">
                    <div className="space-y-2">
                      <div className="h-3 bg-gray-200 rounded-full"></div>
                      <div className="h-3 bg-gray-200 rounded-full w-4/5"></div>
                      <div className="h-3 bg-gray-200 rounded-full w-3/5"></div>
                    </div>
                    
                    <div className="bg-gradient-to-r from-teal-50 to-blue-50 p-4 rounded-lg">
                      <div className="h-2 bg-teal-200 rounded-full mb-2"></div>
                      <div className="h-2 bg-teal-200 rounded-full w-3/4 mb-2"></div>
                      <div className="h-2 bg-blue-200 rounded-full w-2/3"></div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="h-2 bg-gray-200 rounded-full"></div>
                      <div className="h-2 bg-gray-200 rounded-full w-4/5"></div>
                      <div className="h-2 bg-gray-200 rounded-full w-3/5"></div>
                      <div className="h-2 bg-gray-200 rounded-full w-2/3"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      
            {/* Platform Features Section */}
            <section className="section-background-blue">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Une plateforme conçue pour votre succès
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Tout ce dont vous avez besoin pour prendre des décisions d'investissement éclairées, à portée de main.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Gestion de Portefeuille */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group">
              <div className="mb-6 relative overflow-hidden rounded-xl">
                <div className="w-full h-48 bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center relative">
                  <svg className="w-32 h-32 transform group-hover:scale-110 transition-transform duration-300" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {/* Dashboard Background */}
                    <rect x="20" y="40" width="160" height="120" rx="8" fill="url(#grad1)" fillOpacity="0.1"/>
                    <rect x="20" y="40" width="160" height="120" rx="8" stroke="url(#grad1)" strokeWidth="2"/>
                    
                    {/* Charts */}
                    <circle cx="60" cy="80" r="20" fill="none" stroke="#14b8a6" strokeWidth="3" strokeDasharray="40 20"/>
                    <circle cx="60" cy="80" r="15" fill="none" stroke="#3b82f6" strokeWidth="2" strokeDasharray="30 20"/>
                    
                    {/* Bar Chart */}
                    <rect x="100" y="100" width="8" height="20" rx="2" fill="#14b8a6"/>
                    <rect x="112" y="90" width="8" height="30" rx="2" fill="#3b82f6"/>
                    <rect x="124" y="95" width="8" height="25" rx="2" fill="#8b5cf6"/>
                    <rect x="136" y="85" width="8" height="35" rx="2" fill="#f59e0b"/>
                    
                    {/* Trend Line */}
                    <path d="M30 130 Q70 115 110 120 T150 110" stroke="#14b8a6" strokeWidth="2" fill="none"/>
                    <circle cx="30" cy="130" r="2" fill="#14b8a6"/>
                    <circle cx="70" cy="118" r="2" fill="#14b8a6"/>
                    <circle cx="110" cy="120" r="2" fill="#14b8a6"/>
                    <circle cx="150" cy="110" r="2" fill="#14b8a6"/>
                    
                    <defs>
                      <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#14b8a6"/>
                        <stop offset="100%" stopColor="#3b82f6"/>
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute top-2 right-2 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  </div>
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Gestion de Portefeuille Intuitive
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Suivez, analysez et ajustez vos investissements en temps réel grâce à un tableau de bord simple et puissant.
              </p>
            </div>

            {/* Transactions Sécurisées */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group">
              <div className="mb-6 relative overflow-hidden rounded-xl">
                <div className="w-full h-48 bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center relative">
                  <svg className="w-32 h-32 transform group-hover:scale-110 transition-transform duration-300" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {/* Shield */}
                    <path d="M100 30 L130 45 L130 85 Q130 110 100 125 Q70 110 70 85 L70 45 Z" fill="url(#shieldGrad)" stroke="#3b82f6" strokeWidth="2"/>
                    
                    {/* Lock */}
                    <rect x="85" y="75" width="30" height="25" rx="4" fill="#ffffff" stroke="#3b82f6" strokeWidth="2"/>
                    <path d="M92 75 L92 68 Q92 62 98 62 L102 62 Q108 62 108 68 L108 75" fill="none" stroke="#3b82f6" strokeWidth="2"/>
                    <circle cx="100" cy="85" r="3" fill="#3b82f6"/>
                    
                    {/* Security Dots */}
                    <circle cx="100" cy="50" r="2" fill="#ffffff" opacity="0.8"/>
                    <circle cx="110" cy="55" r="1.5" fill="#ffffff" opacity="0.6"/>
                    <circle cx="90" cy="55" r="1.5" fill="#ffffff" opacity="0.6"/>
                    
                    {/* Encrypted Data Lines */}
                    <g opacity="0.4">
                      <rect x="40" y="140" width="20" height="2" rx="1" fill="#3b82f6"/>
                      <rect x="65" y="140" width="15" height="2" rx="1" fill="#3b82f6"/>
                      <rect x="85" y="140" width="25" height="2" rx="1" fill="#3b82f6"/>
                      <rect x="115" y="140" width="18" height="2" rx="1" fill="#3b82f6"/>
                      <rect x="140" y="140" width="22" height="2" rx="1" fill="#3b82f6"/>
                      
                      <rect x="50" y="150" width="18" height="2" rx="1" fill="#6366f1"/>
                      <rect x="75" y="150" width="12" height="2" rx="1" fill="#6366f1"/>
                      <rect x="95" y="150" width="20" height="2" rx="1" fill="#6366f1"/>
                      <rect x="125" y="150" width="25" height="2" rx="1" fill="#6366f1"/>
                    </g>
                    
                    <defs>
                      <linearGradient id="shieldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.2"/>
                        <stop offset="100%" stopColor="#1d4ed8" stopOpacity="0.3"/>
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute top-2 right-2 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <Shield className="w-3 h-3 text-blue-600" />
                  </div>
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Transactions Sécurisées
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Nous utilisons les dernières technologies de cryptage pour garantir la sécurité de vos fonds et de vos données personnelles.
              </p>
            </div>

            {/* Analyses par IA */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group">
              <div className="mb-6 relative overflow-hidden rounded-xl">
                <div className="w-full h-48 bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center relative">
                  <svg className="w-32 h-32 transform group-hover:scale-110 transition-transform duration-300" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {/* AI Brain */}
                    <circle cx="100" cy="90" r="45" fill="url(#brainGrad)" fillOpacity="0.1" stroke="url(#brainGrad)" strokeWidth="2"/>
                    
                    {/* Neural Network */}
                    <g stroke="#8b5cf6" strokeWidth="1.5" opacity="0.8">
                      {/* Neurons */}
                      <circle cx="70" cy="70" r="4" fill="#8b5cf6"/>
                      <circle cx="130" cy="70" r="4" fill="#8b5cf6"/>
                      <circle cx="85" cy="100" r="4" fill="#8b5cf6"/>
                      <circle cx="115" cy="100" r="4" fill="#8b5cf6"/>
                      <circle cx="100" cy="120" r="4" fill="#8b5cf6"/>
                      
                      {/* Connections */}
                      <line x1="70" y1="70" x2="85" y2="100" strokeOpacity="0.6"/>
                      <line x1="70" y1="70" x2="115" y2="100" strokeOpacity="0.4"/>
                      <line x1="130" y1="70" x2="115" y2="100" strokeOpacity="0.6"/>
                      <line x1="130" y1="70" x2="85" y2="100" strokeOpacity="0.4"/>
                      <line x1="85" y1="100" x2="100" y2="120" strokeOpacity="0.8"/>
                      <line x1="115" y1="100" x2="100" y2="120" strokeOpacity="0.8"/>
                    </g>
                    
                    {/* Data Flow */}
                    <g className="animate-pulse">
                      <circle cx="50" cy="45" r="2" fill="#f59e0b">
                        <animate attributeName="opacity" values="0.3;1;0.3" dur="2s" repeatCount="indefinite"/>
                      </circle>
                      <circle cx="150" cy="45" r="2" fill="#10b981">
                        <animate attributeName="opacity" values="1;0.3;1" dur="2s" repeatCount="indefinite"/>
                      </circle>
                      <circle cx="60" cy="140" r="2" fill="#ef4444">
                        <animate attributeName="opacity" values="0.3;1;0.3" dur="1.5s" repeatCount="indefinite"/>
                      </circle>
                      <circle cx="140" cy="140" r="2" fill="#3b82f6">
                        <animate attributeName="opacity" values="1;0.3;1" dur="1.5s" repeatCount="indefinite"/>
                      </circle>
                    </g>
                    
                    {/* Analysis Lines */}
                    <g opacity="0.6">
                      <path d="M30 160 Q100 150 170 165" stroke="#8b5cf6" strokeWidth="2" fill="none" strokeDasharray="5 5">
                        <animate attributeName="stroke-dashoffset" values="0;-10;0" dur="3s" repeatCount="indefinite"/>
                      </path>
                    </g>
                    
                    <defs>
                      <linearGradient id="brainGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#8b5cf6"/>
                        <stop offset="50%" stopColor="#ec4899"/>
                        <stop offset="100%" stopColor="#f59e0b"/>
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute top-2 right-2 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
                    <TrendingUp className="w-3 h-3 text-purple-600" />
                  </div>
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Analyses par IA
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Profitez d'analyses de marché et de recommandations personnalisées générées par notre intelligence artificielle.
              </p>
            </div>
          </div>
        </div>
      </section>
      


      {/* Services Section */}
      <section id="services" className="section-background-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Nos solutions digitales d'investissement et d'épargne
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Plateforme 100% en ligne avec des outils intelligents pour optimiser vos investissements 
              et construire votre patrimoine depuis chez vous
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Simulateur */}
            <div className="service-card-teal">
              <div className="flex items-center mb-6">
                <div className="service-icon-teal">
                  <Calculator className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Simulateur SOeasi</h3>
              </div>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Outil digital intelligent qui évalue votre profil d'investisseur et vous propose 
                des allocations personnalisées adaptées à vos objectifs. 100% en ligne, sans rendez-vous.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-gray-700">
                  <div className="w-2 h-2 bg-teal-500 rounded-full mr-3"></div>
                  Analyse de profil complète
                </li>
                <li className="flex items-center text-gray-700">
                  <div className="w-2 h-2 bg-teal-500 rounded-full mr-3"></div>
                  Recommandations personnalisées
                </li>
                <li className="flex items-center text-gray-700">
                  <div className="w-2 h-2 bg-teal-500 rounded-full mr-3"></div>
                  Simulation de rendements
                </li>
              </ul>
              <button
                onClick={onStartSimulator}
                className="w-full btn-primary"
              >
                <span>Démarrer la simulation</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {/* Conseiller IA */}
            <div className="service-card-purple">
              <div className="flex items-center mb-6">
                <div className="service-icon-purple">
                  <div className="relative">
                    <Bot className="w-6 h-6 text-white" />
                    <Sparkles className="w-3 h-3 absolute -top-1 -right-1 text-yellow-300" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Conseiller IA</h3>
              </div>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Conseiller virtuel disponible 24h/24 qui analyse votre situation patrimoniale 
                et vous propose des stratégies personnalisées. Accessible depuis votre smartphone ou ordinateur.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-gray-700">
                  <div className="w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
                  Analyse patrimoniale complète
                </li>
                <li className="flex items-center text-gray-700">
                  <div className="w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
                  Questions-réponses en temps réel
                </li>
                <li className="flex items-center text-gray-700">
                  <div className="w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
                  Conseils personnalisés
                </li>
              </ul>
              <button
                onClick={onStartSimulator}
                className="w-full btn-primary"
              >
                <span>Parler au conseiller</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>
 {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={onStartGuidedInvestment}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-300 flex items-center space-x-3 justify-center shadow-lg hover:shadow-2xl transform hover:-translate-y-1 relative overflow-hidden group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-400 opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                  <Target className="w-6 h-6 relative z-10" />
                  <span className="relative z-10">Assistant Personnalisé</span>
                  <div className="absolute -top-1 -right-1 bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-1 rounded-full">
                    NOUVEAU
                  </div>
                </button>
                <button
                  onClick={onStartSimulateurInvestissement}
                  className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-green-600 hover:to-emerald-600 transition-all duration-300 flex items-center space-x-3 justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  <TrendingUp className="w-5 h-5" />
                  <span>Simulez votre investissement</span>
                </button>
                <button
                  onClick={onStartSimulator}
                  className="btn-primary"
                >
                  <Calculator className="w-5 h-5" />
                  <span>Bilan patrimonial</span>
                </button>
              </div>
    </div>
  );
};

export default NosSimulateurs;